import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import {
  AutosaveStorage,
  autosaveStorageKey,
  DEFAULT_AUTOSAVE_INTERVAL_MS,
  type AutosaveIdentity,
} from "@/lib/autosave"

export type AutosavePhase =
  | "idle"
  | "saved"
  | "restored"
  | "discarded"
  | "expired"
  | "corrupt"
  | "incompatible"
  | "error"
  | "quota-exceeded"

export interface AutosaveStatus {
  phase: AutosavePhase
  lastSavedAt?: number
  error?: Error
}

export interface UseAutosaveOptions<T> {
  identity: AutosaveIdentity
  state: T
  dirty: boolean
  restore: (state: T) => void
  enabled?: boolean
  intervalMs?: number
  ttlMs?: number
  storage?: Storage
}

export interface UseAutosaveResult {
  status: AutosaveStatus
  save: () => boolean
  saveIfDirty: () => boolean
  discard: () => boolean
  clear: () => boolean
}

function hasCompleteIdentity(identity: AutosaveIdentity): boolean {
  return (
    typeof identity.workspaceId === "string" &&
    identity.workspaceId.trim() !== "" &&
    typeof identity.userId === "string" &&
    identity.userId.trim() !== "" &&
    typeof identity.formId === "string" &&
    identity.formId.trim() !== "" &&
    (identity.recordId === undefined ||
      (typeof identity.recordId === "string" && identity.recordId.trim() !== ""))
  )
}

export function useAutosave<T>({
  identity,
  state,
  dirty,
  restore,
  enabled = true,
  intervalMs = DEFAULT_AUTOSAVE_INTERVAL_MS,
  ttlMs,
  storage,
}: UseAutosaveOptions<T>): UseAutosaveResult {
  const scopeEnabled = enabled && hasCompleteIdentity(identity)
  const identityKey = scopeEnabled ? autosaveStorageKey(identity) : "disabled"
  const service = useMemo(
    () => new AutosaveStorage({ storage, ttlMs }),
    [storage, ttlMs],
  )
  const [status, setStatus] = useState<AutosaveStatus>({ phase: "idle" })
  const latest = useRef({
    identity,
    state,
    dirty,
    restore,
    enabled: scopeEnabled,
  })
  const serviceRef = useRef(service)
  // A successful submit clears its draft before the form's dirty state is
  // necessarily reset. Do not let a closing dialog recreate that draft.
  const clearedScopeKey = useRef<string | null>(null)
  // Discard differs from successful submit: suppress the unchanged recovered
  // snapshot, but re-arm autosave as soon as the user edits it again.
  const discardedStateFingerprint = useRef<string | null>(null)

  const fingerprint = useCallback((value: T): string | null => {
    try {
      return JSON.stringify(value)
    } catch {
      return null
    }
  }, [])

  const shouldSuppressSave = useCallback(
    (current: { identity: AutosaveIdentity; state: T }): boolean => {
      const scopeKey = autosaveStorageKey(current.identity)
      if (clearedScopeKey.current !== scopeKey) return false
      if (
        discardedStateFingerprint.current !== null &&
        fingerprint(current.state) !== discardedStateFingerprint.current
      ) {
        clearedScopeKey.current = null
        discardedStateFingerprint.current = null
        return false
      }
      return true
    },
    [fingerprint],
  )

  const persist = useCallback(
    (
      storageService: AutosaveStorage,
      current: { identity: AutosaveIdentity; state: T },
    ): boolean => {
      const result = storageService.write(current.identity, current.state)
      if (result.outcome === "saved") {
        setStatus({ phase: "saved", lastSavedAt: result.savedAt })
        return true
      }
      setStatus({
        phase: result.outcome,
        error: result.error,
      })
      return false
    },
    [],
  )

  const write = useCallback(
    (current: { identity: AutosaveIdentity; state: T }): boolean => {
      const saved = persist(service, current)
      if (saved) {
        clearedScopeKey.current = null
        discardedStateFingerprint.current = null
      }
      return saved
    },
    [persist, service],
  )

  // Do not update this ref while rendering: React may abandon a concurrent
  // render, and a lifecycle handler must only ever save committed form state.
  // A layout effect runs before the browser can dispatch pagehide, unlike a
  // passive effect which could leave that handler one committed render behind.
  useLayoutEffect(() => {
    const previous = latest.current
    const previousIdentityKey = previous.enabled
      ? autosaveStorageKey(previous.identity)
      : null
    const nextIdentityKey = scopeEnabled ? autosaveStorageKey(identity) : null
    const identityChanged =
      previousIdentityKey !== null &&
      nextIdentityKey !== null &&
      previousIdentityKey !== nextIdentityKey
    // On disable, the current render may already contain reset form values.
    // The same is true when a form switches records. Flush the prior committed
    // value to its own scope before adopting any state from the new identity.
    if (
      previous.enabled &&
      (!scopeEnabled || identityChanged) &&
      previous.dirty &&
      !shouldSuppressSave(previous)
    ) {
      persist(serviceRef.current, previous)
    }

    // A normal successful form reset makes a future edit eligible to save.
    // Do this after the disable flush so clear + close cannot recreate a draft.
    if (
      identityChanged ||
      (scopeEnabled && !dirty && clearedScopeKey.current === nextIdentityKey)
    ) {
      clearedScopeKey.current = null
      discardedStateFingerprint.current = null
    }
    latest.current = {
      identity,
      state,
      dirty,
      restore,
      enabled: scopeEnabled,
    }
    serviceRef.current = service
  })

  const save = useCallback((): boolean => {
    const current = latest.current
    if (!current.enabled) return false
    return write(current)
  }, [write])

  const saveIfDirty = useCallback((): boolean => {
    const current = latest.current
    if (
      !current.enabled ||
      !current.dirty ||
      shouldSuppressSave(current)
    ) {
      return false
    }
    return save()
  }, [save])

  const flushIfDirty = useCallback((): boolean => {
    const current = latest.current
    if (
      !current.enabled ||
      !current.dirty ||
      shouldSuppressSave(current)
    ) {
      return false
    }
    return write(current)
  }, [write])

  const clear = useCallback((): boolean => {
    const current = latest.current
    if (!current.enabled) return false
    const error = service.clear(current.identity)
    if (error) {
      setStatus({ phase: "error", error })
      return false
    }
    clearedScopeKey.current = autosaveStorageKey(current.identity)
    discardedStateFingerprint.current = null
    setStatus({ phase: "idle" })
    return true
  }, [service])

  const discard = useCallback((): boolean => {
    const current = latest.current
    if (!current.enabled) return false
    const error = service.clear(current.identity)
    if (error) {
      setStatus({ phase: "error", error })
      return false
    }
    clearedScopeKey.current = autosaveStorageKey(current.identity)
    discardedStateFingerprint.current = fingerprint(current.state)
    setStatus({ phase: "discarded" })
    return true
  }, [fingerprint, service])

  useEffect(() => {
    setStatus({ phase: "idle" })
    if (!scopeEnabled) return

    const result = service.read<T>(identity)
    if (result.outcome === "restored") {
      try {
        latest.current.restore(result.data)
        setStatus({ phase: "restored", lastSavedAt: result.savedAt })
      } catch (error) {
        setStatus({
          phase: "error",
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }
    } else if (
      result.outcome === "expired" ||
      result.outcome === "corrupt" ||
      result.outcome === "incompatible"
    ) {
      setStatus({ phase: result.outcome })
    } else if (result.outcome === "error") {
      setStatus({ phase: "error", error: result.error })
    }
  }, [scopeEnabled, identityKey, service])

  useEffect(() => {
    if (!scopeEnabled) return
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
      throw new TypeError("Autosave interval must be greater than zero")
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushIfDirty()
    }

    const timer = window.setInterval(flushIfDirty, intervalMs)
    window.addEventListener("pagehide", flushIfDirty)
    document.addEventListener("visibilitychange", onVisibilityChange)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener("pagehide", flushIfDirty)
      document.removeEventListener("visibilitychange", onVisibilityChange)
    }
  }, [scopeEnabled, identityKey, intervalMs, flushIfDirty])

  useEffect(() => {
    return () => {
      const current = latest.current
      if (
        current.enabled &&
        current.dirty &&
        !shouldSuppressSave(current)
      ) {
        // Avoid state updates during unmount, but still synchronously persist
        // the last committed form state.
        serviceRef.current.write(current.identity, current.state)
      }
    }
  }, [shouldSuppressSave])

  return { status, save, saveIfDirty, discard, clear }
}